import { Bell, Search, User, Menu, CheckCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { notificationsApi } from "@/services/api";
import Input from "../ui/Input";

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { user, company } = useAuthStore();

  const { data, refetch } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => notificationsApi.list({ page: 1, per_page: 8 }),
    enabled: Boolean(user),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const markAllMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => refetch(),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => refetch(),
  });

  const notifications = data?.data?.items || [];
  const unreadCount = data?.data?.unread_count || 0;

  return (
    <header className="h-14 sm:h-16 bg-bg-secondary/50 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
      {/* Mobile menu button */}
      <button
        onClick={onMenuClick}
        className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors lg:hidden mr-2"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Search */}
      <div className="flex-1 max-w-xl">
        {searchOpen ? (
          <Input
            placeholder="Buscar orçamentos, clientes..."
            leftIcon={<Search className="w-5 h-5" />}
            className="max-w-md"
            autoFocus
            onBlur={() => setSearchOpen(false)}
          />
        ) : (
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 text-text-muted hover:text-text-secondary transition-colors"
          >
            <Search className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Buscar...</span>
            <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-1 text-xs bg-white/5 rounded border border-white/10">
              <span>⌘</span>K
            </kbd>
          </button>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Company name */}
        {company && (
          <span className="text-sm text-text-secondary hidden lg:block">
            {company.name}
          </span>
        )}

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotificationsOpen((prev) => !prev)}
            className="relative p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-accent-primary text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5">
                {unreadCount}
              </span>
            )}
          </button>

          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-bg-secondary/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <p className="text-sm font-semibold text-text-primary">
                  Notificações
                </p>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllMutation.mutate()}
                    className="text-xs text-accent-primary hover:text-accent-secondary"
                  >
                    Marcar todas como lidas
                  </button>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-text-secondary text-sm">
                    Nenhuma notificação no momento.
                  </div>
                ) : (
                  notifications.map((notification: any) => (
                    <button
                      key={notification.id}
                      onClick={() =>
                        notification.read_at
                          ? undefined
                          : markReadMutation.mutate(notification.id)
                      }
                      className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`mt-1 h-2.5 w-2.5 rounded-full ${notification.read_at ? "bg-white/10" : "bg-accent-primary"}`}
                        />
                        <div className="flex-1">
                          <p className="text-sm text-text-primary font-semibold">
                            {notification.title}
                          </p>
                          {notification.message && (
                            <p className="text-xs text-text-muted mt-1">
                              {notification.message}
                            </p>
                          )}
                          <div className="flex items-center gap-1 mt-2 text-[11px] text-text-muted">
                            <CheckCircle className="w-3 h-3" />
                            {new Date(notification.created_at).toLocaleString(
                              "pt-BR",
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Avatar */}
        <Link to="/admin/profile" className="flex items-center gap-3">
          <button className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white font-semibold text-sm hover:from-accent-secondary hover:to-accent-primary transition-all hover:ring-2 hover:ring-accent-primary hover:ring-offset-2 hover:ring-offset-bg-primary">
            {user?.name?.charAt(0).toUpperCase() || (
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </button>
        </Link>
      </div>
    </header>
  );
}
